<!--
  ~ Copyright (c) GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->
<fab-loading-spinner [hidden]="(!showSpinner)" color="primary"></fab-loading-spinner>
<div id="header-container">
  <h1 id="header-text">Create Propulsion Scenario</h1>
</div>

<mat-stepper labelPosition="bottom" linear="true" class="stepper" #stepper>
  <mat-step [completed]="firstForm.form.valid">
    <ng-template matStepLabel>Enter Information</ng-template>
    <form #firstForm="ngForm">
      <div class="d-flex flex-column gap-16">
        <mat-slide-toggle color="primary" [(ngModel)]="isTVP"
                          labelPosition="before"
                          *ngIf="stateService.hasAuthority(VIEW_AUTHORITY.regionalAdminAuthority)"
                          (toggleChange)="clearSecondPage()" ngDefaultControl name="isTVP"><h3>Is TVP Scenario</h3>
        </mat-slide-toggle>
        <mat-form-field class="w-50">
          <mat-label>Scenario Name</mat-label>
          <input matInput #scenarioNameInput name="scenarioName" required [maxlength]="nameMaxLength"
                 [(ngModel)]="scenarioName">
          <mat-hint align="start">{{ scenarioNameInput.value.length }}/{{ nameMaxLength }}Characters
          </mat-hint>
        </mat-form-field>
        <mat-form-field class="w-50">
          <mat-label>Scenario Assumption</mat-label>
          <input matInput #assmInput name="assm" required [maxLength]="assumptionMaxLength" [(ngModel)]="assumption">
          <mat-hint align="start">{{ assmInput.value.length }}/{{ assumptionMaxLength }} Characters</mat-hint>
        </mat-form-field>
      </div>
    </form>
    <footer class="footer">
      <button [disabled]="!firstForm.form.valid" matStepperNext mat-flat-button color="primary" class="action-button">
        Next
      </button>
    </footer>
  </mat-step>

  <mat-step>
    <ng-template matStepLabel>Choose Source</ng-template>
    <ng-template matStepContent>
      <div class="d-flex flex-column gap-16">
        <mat-form-field class="w-25" subscriptSizing="dynamic">
          <mat-label>Select Source</mat-label>
          <mat-select [(ngModel)]="selectedSource" name="selectedSource" (ngModelChange)="onSourceChange()">
            <mat-option *ngFor="let source of sourceList" [value]="source"
                        [disabled]="(isTVP && source === fromTemplate)">{{ source }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field *ngIf="selectedSource === fromPortfolio && isTVP">
          <mat-label>Select Architecture</mat-label>
          <mat-select [(ngModel)]="selectedArchitecture" (ngModelChange)="getVehiclePrograms(selectedArchitecture)"
                      name="selectedArchitecture">
            <mat-option *ngFor="let architecture of architectures"
                        [value]="architecture">{{ architecture }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field class="w-25" subscriptSizing="dynamic" *ngIf="selectedSource === fromPortfolio && !isTVP">
          <mat-label>Select Fcorg</mat-label>
          <mat-select [(ngModel)]="selectedFcorg" name="selectedFcorg">
            <mat-option *ngFor="let fcorg of fcorgs" [value]="fcorg">{{ fcorg }}</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="table-div d-flex flex-column"
             *ngIf="selectedSource === fromPortfolio && vehiclePrograms?.length > 0">
          <div class="h-100">
            <ag-grid-angular
              style="height: 100%;"
              class="ag-theme-quartz"
              [rowData]="vehiclePrograms"
              [columnDefs]="vehicleProgramColumnDefinition"
              [autoSizeStrategy]="autoSizeStrategy"
              [rowSelection]="rowSelection"
              (rowSelected)="onProgramSelect($event)"
            />
          </div>
        </div>

        <div class="table-div d-flex flex-column" *ngIf="selectedSource === fromScenario">
          <div class="h-100">
            <app-propulsion-scenario-select titleText="Select Scenario"
                                            #propulsionScenarioSelect
                                            [isMultiSelect]=false
                                            [includeTVP]=isTVP
                                            [validFcorgs]="validFcorgs"
                                            (confirmEvent)="onPropulsionScenarioSelect($event)"
            ></app-propulsion-scenario-select>
          </div>
        </div>

        <div *ngIf="selectedSource === fromTemplate">
          <div class="drag-and-drop-container">
            <h4>
              <span class="template-dropzone-label">Please Select File to select worksheets from. </span> (Required
              Format: .xls or .xlsx)
            </h4>
          </div>
          <div (files)="upload($event)" appDrag class="dropzone">
            <div class="text-wrapper">
              <div class="centered">Drag and Drop Files Here</div>
              <p class="centered">or</p>
              <div>
                <app-upload-button (eventEmitter)="upload($event)" [apiUrl]="excelService.excelSheetUrl"
                                   class="upload centered app-upload-button">
                </app-upload-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer class="footer gap-8">
        <button mat-stroked-button class="action-button" matStepperPrevious>Back</button>
        <button [disabled]="!isSecondPageComplete()"
                matStepperNext mat-flat-button color="primary" class="action-button">
          Next
        </button>
      </footer>
    </ng-template>
  </mat-step>
  <mat-step>
    <ng-template matStepLabel>Confirm Selection</ng-template>
    <ng-template matStepContent>
      <mat-action-list>
        <mat-list-item (click)="updateScenarioName(scenarioName,nameMaxLength)"
                       (keydown)="updateScenarioName(scenarioName, nameMaxLength)">
          <span matListItemTitle>Scenario Name
            <mat-icon class="confirm-icons">create</mat-icon>
          </span>
          <span matListItemLine>{{ scenarioName }}</span>
        </mat-list-item>
        <mat-divider></mat-divider>
        <mat-list-item (click)="updateAssumption(assumption,assumptionMaxLength)"
                       (keydown)="updateAssumption(assumption,assumptionMaxLength)">
          <span matListItemTitle>Assumption
            <mat-icon class="confirm-icons">create</mat-icon>
          </span>
          <span matListItemLine>{{ assumption }}</span>
        </mat-list-item>
        <mat-divider></mat-divider>
        <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
          <span matListItemTitle>Base Scenario Source
            <mat-icon class="confirm-icons">chevron_left</mat-icon>
          </span>
          <span matListItemLine>{{ selectedSource }}</span>
        </mat-list-item>
        <mat-divider></mat-divider>

        <div *ngIf="selectedSource === fromScenario">
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Selected Scenario
              <mat-icon class="confirm-icons">chevron_left</mat-icon>
            </span>
            <span matListItemLine>{{ selectedPropulsionScenario.forecastOrganization }}</span>
            <span matListItemLine>{{ selectedPropulsionScenario.id }}
              - {{ selectedPropulsionScenario.propulsionScenarioName }}</span>
          </mat-list-item>
          <mat-divider></mat-divider>
        </div>

        <div *ngIf="selectedSource === fromPortfolio && !this.isTVP">
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Selected Forecast Organization
              <mat-icon class="confirm-icons">chevron_left</mat-icon>
            </span>
            <span matListItemLine>{{ selectedFcorg }}</span>
          </mat-list-item>
          <mat-divider></mat-divider>
        </div>

        <div *ngIf="selectedSource === fromPortfolio && this.isTVP">
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Vehicle Architecture</span>
            <span matListItemLine>{{ selectedArchitecture }}</span>
          </mat-list-item>
          <mat-divider></mat-divider>
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Vehicle Programs</span>
            <span matListItemLine *ngFor="let program of selectedVehiclePrograms">{{ program.mktDiv }}
              | {{ program.brand }} | {{ program.prgmCode }} | {{ program.styleCode }}
              | {{ program.bodyStyle }}</span>
          </mat-list-item>
          <mat-divider></mat-divider>
        </div>

        <div *ngIf="selectedSource === fromTemplate">
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Selected Template File
              <mat-icon class="confirm-icons">chevron_left</mat-icon>
            </span>
            <span matListItemLine>{{ file.name }}</span>
          </mat-list-item>
          <mat-divider></mat-divider>
          <mat-list-item (click)="stepper.previous()" (keydown)="stepper.previous()">
            <span matListItemTitle>Selected Sheets
              <mat-icon class="confirm-icons">chevron_left</mat-icon>
            </span>
            <span matListItemLine *ngFor="let sheet of sheets.selectedOptions">{{ sheet }}</span>
          </mat-list-item>
        </div>

      </mat-action-list>
      <footer class="footer gap-8">
        <button mat-stroked-button class="action-button" matStepperPrevious>Back</button>
        <button matStepperNext mat-flat-button color="primary" class="action-button"
                (click)="createPropulsionScenario()">
          Create
        </button>
      </footer>
    </ng-template>
  </mat-step>
</mat-stepper>

